import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import { useSessionStore } from '@features/auth/store/useSessionStore';
import { buildAppUrl } from '@constants/buildAppUrl';
import { publicRoutes } from '@constants/publicRoutesConnect';

let isForcedLogoutInProgress = false;

const redirectToLogin = (reason: string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const loginUrl = buildAppUrl(
    `${publicRoutes.LOGIN}?reason=${encodeURIComponent(reason)}`,
  );
  window.location.replace(loginUrl);
};

const forceLogout = async (reason: string): Promise<void> => {
  if (isForcedLogoutInProgress) {
    return;
  }

  isForcedLogoutInProgress = true;
  try {
    const { authToken, secondaryAuthToken } = useSessionStore.getState();
    // Only call backend logout if we still have a valid token to authenticate with.
    if (authToken != null || secondaryAuthToken != null) {
      await useSessionStore.getState().endSession();
    } else {
      useSessionStore.getState().clearSession();
    }
  } catch {
    // Best effort — token may already be dead.
    useSessionStore.getState().clearSession();
  } finally {
    redirectToLogin(reason);
    // Small cooldown to avoid multiple redirects from concurrent 401 responses.
    setTimeout(() => {
      isForcedLogoutInProgress = false;
    }, 1000);
  }
};

export const createAxiosInstance = (baseURL: string): AxiosInstance => {
  const isAuthBase =
    baseURL.includes('20060') || baseURL.includes('/auth');

  const instance = axios.create({
    baseURL,
    withCredentials: isAuthBase,
  });

  instance.interceptors.request.use((config) => {
    const { authToken, secondaryAuthToken } = useSessionStore.getState();

    const isAuthApi =
      config.baseURL?.includes('20060') === true ||
      config.baseURL?.includes('/auth') === true ||
      isAuthBase;

    const token = isAuthApi ? secondaryAuthToken : authToken;

    if (typeof token === 'string' && token !== '' && config.headers != null) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      if (error.response?.status !== 401) {
        return await Promise.reject(error);
      }

      const requestConfig = error.config as
        | (InternalAxiosRequestConfig & { _retry?: boolean })
        | undefined;

      const isLoginRequest = requestConfig?.url?.includes('/login') === true;
        
      if (isLoginRequest) {
        return await Promise.reject(error);
      }

      if (requestConfig == null) {
        await forceLogout('token');
        return await Promise.reject(error);
      }

      const isAuthApi =
        requestConfig.baseURL?.includes('20060') === true ||
        requestConfig.baseURL?.includes('/auth') === true ||
        isAuthBase;

      const isRefreshRequest =
        requestConfig.url?.includes('/user/refresh') === true;

      // Already retried or refresh endpoint itself failed — give up.
      if (isRefreshRequest || requestConfig._retry === true) {
        await forceLogout('token');
        return await Promise.reject(error);
      }

      requestConfig._retry = true;

      if (isAuthApi) {
        // Auth API 401 → refresh secondaryAuthToken and retry.
        const refreshed = await useSessionStore.getState().refreshSession();

        if (!refreshed) {
          await forceLogout('refresh');
          return await Promise.reject(error);
        }

        const { secondaryAuthToken } = useSessionStore.getState();
        if (requestConfig.headers != null) {
          requestConfig.headers.Authorization = `Bearer ${secondaryAuthToken}`;
        }

        return await instance.request(requestConfig);
      }

      // Non-auth API 401 (edge/contacts/sync) → authToken has no refresh,
      // but retry once in case it was a transient failure (race condition, etc).
      const { authToken } = useSessionStore.getState();
      if (requestConfig.headers != null && typeof authToken === 'string' && authToken !== '') {
        requestConfig.headers.Authorization = `Bearer ${authToken}`;
      }

      try {
        return await instance.request(requestConfig);
      } catch {
        await forceLogout('edge-token-expired');
        return await Promise.reject(error);
      }
    },
  );

  return instance;
};
